import { Component, effect, inject, signal, viewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import {
  injectTrialSettingByName,
  injectUpdateTrialSettingMutation,
} from '@models/trial-settings/api/trial-settings.api';

import { ButtonComponent } from '@shared/components/button/button.component';
import { InputMaskComponent } from '@shared/components/input-mask/input-mask.component';
import { TrialSettingKey } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';

@Component({
  imports: [InputMaskComponent, ReactiveFormsModule, ButtonComponent],
  selector: 'aux-patient-invoiceables-buffer-input',
  template: `
    <div>
      <aux-input-mask
        #patientInvoiceablesInputMask
        [formControl]="patientInvoiceablesFc"
        maskType="limitedPercentage"
        label="Patient Invoiceable Buffer %"
        [precision]="2"
        inputClassName="!outline-none !shadow-none !text-lg text-aux-gray-darkest !font-normal h-[50px]"
      >
        <div class="flex items-center justify-end">
          @if (editMode()) {
            <aux-button
              class="mr-2"
              variant="primary"
              label="Save"
              (click)="onSave()"
              [disabled]="patientInvoiceablesFc.invalid || isLoading()"
            />

            <aux-button
              variant="tertiary"
              label="Cancel"
              (click)="onCancel()"
              [disabled]="isLoading()"
            />
          } @else {
            <aux-button
              variant="custom"
              icon="Pencil"
              svgAttributes="text-aux-blue pt-0.5"
              (click)="goEditMode()"
            />
          }
        </div>
      </aux-input-mask>
    </div>
  `,
})
export class PatientInvoiceablesBufferInput {
  private overlayService = inject(OverlayService);

  patientInvoiceablesPercentageSetting = injectTrialSettingByName(
    TrialSettingKey.TRIAL_SETTING_PATIENT_INVOICEABLE_PERCENTAGE
  );
  updateSiteSettingMutation = injectUpdateTrialSettingMutation();

  editMode = signal(false);
  isLoading = signal(false);

  patientInvoiceablesInputMask = viewChild<InputMaskComponent>('patientInvoiceablesInputMask');

  initialValue = 0;
  patientInvoiceablesFc = new FormControl<null | number>(null);
  _setDefaultDiscontinuationRate = effect(() => {
    const patientInvoiceablesPercentage =
      this.patientInvoiceablesPercentageSetting.setting()?.setting_value ?? null;

    this.patientInvoiceablesFc.setValue(Number(patientInvoiceablesPercentage) || 0);
    this.initialValue = this.patientInvoiceablesFc.value ?? 0;
  });
  _toggleDisabled = effect(() => {
    if (!this.editMode()) {
      this.patientInvoiceablesFc.disable();
      this.onCancel();
    } else {
      this.patientInvoiceablesFc.enable();
    }
  });

  goEditMode() {
    this.editMode.set(true);

    const FOCUS_DELAY = 100;

    setTimeout(() => {
      this.patientInvoiceablesInputMask()?.inputElement()?.nativeElement?.focus();
    }, FOCUS_DELAY);
  }

  onCancel() {
    this.editMode.set(false);
    this.patientInvoiceablesFc.setValue(this.initialValue);
  }

  onSave() {
    if (this.patientInvoiceablesFc.invalid) {
      return;
    }

    this.isLoading.set(true);
    try {
      this.updateSiteSettingMutation.mutate({
        setting_key: TrialSettingKey.TRIAL_SETTING_PATIENT_INVOICEABLE_PERCENTAGE,
        setting_value: this.patientInvoiceablesFc.value?.toString() || '0',
      });

      this.editMode.set(false);
      this.initialValue = this.patientInvoiceablesFc.value ?? 0;
      this.overlayService.success('Successfully saved!');
    } catch (error) {
      this.overlayService.error('Failed to update invoiceables buffer');
      console.error('Failed to update invoiceables buffer:', error);
    } finally {
      this.isLoading.set(false);
    }
  }
}
